/**
 * Thin re-export layer over react-router-dom. This is the ONLY module in the
 * monorepo that is permitted to import from `react-router-dom` directly.
 * Everything else consumes routing primitives from `@suntex.core/nav`.
 */
import { createBrowserRouter as createReactRouter } from 'react-router-dom';

import { isBuildStale, startBuildVersionWatch } from './buildVersion';

export {
  isRouteErrorResponse,
  Link,
  MemoryRouter,
  Navigate,
  NavLink,
  Outlet,
  Route,
  RouterProvider,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useRouteError,
  useSearchParams,
} from 'react-router-dom';

// Full reload on the next pathname change once a newer deploy is detected.
export function createBrowserRouter(
  ...args: Parameters<typeof createReactRouter>
): ReturnType<typeof createReactRouter> {
  const router = createReactRouter(...args);
  startBuildVersionWatch();
  let pathname = router.state.location.pathname;
  const unsubscribe = router.subscribe((state) => {
    if (state.location.pathname === pathname) return;
    pathname = state.location.pathname;
    if (isBuildStale()) window.location.reload();
  });
  const dispose = router.dispose.bind(router);
  router.dispose = () => {
    unsubscribe();
    dispose();
  };
  return router;
}

export type {
  Location,
  NavigateFunction,
  NavigateOptions,
  RouteObject,
  To,
} from 'react-router-dom';
